import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, KeyboardEvent as ReactKeyboardEvent } from 'react';
import { popover_position } from '../popover_position';
import type { PopoverPos } from '../popover_position';
import { normalize_query, user_matches, user_option_label } from './user_match';
import type { ComboUser } from './user_match';

export type { ComboUser } from './user_match';

interface Props {
  id: string;
  users: ComboUser[];
  // Selected uuid, '' for none. Setting it to '' from outside clears the input.
  value: string;
  on_change: (uuid: string) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  width?: number;
  max_rows?: number;
}

const ROW_HEIGHT = 34;

const INPUT_STYLE: CSSProperties = {
  background: 'var(--surface-card)',
  color: 'var(--text-primary)',
  border: '1px solid var(--border-default)',
  borderRadius: 6,
  padding: '6px 10px',
  margin: 0,
  width: '100%'
};

function Highlight({ text, query }: { text: string; query: string }) {
  const i = query ? text.toLowerCase().indexOf(query) : -1;
  if (i < 0) return <span>{text}</span>;
  return (
    <span>
      {text.slice(0, i)}
      <span style={{ fontWeight: 600 }}>{text.slice(i, i + query.length)}</span>
      {text.slice(i + query.length)}
    </span>
  );
}

function UserCombobox({
  id,
  users,
  value,
  on_change,
  label = 'User',
  placeholder = 'Type a name or email',
  disabled = false,
  width = 360,
  max_rows = 8
}: Props) {
  const [text, set_text] = useState('');
  const [open, set_open] = useState(false);
  const [active, set_active] = useState(-1);
  const [pos, set_pos] = useState<PopoverPos | null>(null);
  const root_ref = useRef<HTMLDivElement>(null);
  const input_ref = useRef<HTMLInputElement>(null);
  const list_ref = useRef<HTMLUListElement>(null);
  // The uuid this input last showed as a selection, so an external reset to ''
  // clears the text but the user's own typing (which also clears the value) does not.
  const shown_ref = useRef('');

  const selected = useMemo(() => users.find((u) => u.uuid === value) || null, [users, value]);

  useEffect(() => {
    if (selected) {
      set_text(user_option_label(selected));
      shown_ref.current = selected.uuid;
    } else if (shown_ref.current) {
      set_text('');
      shown_ref.current = '';
    }
  }, [selected]);

  // While a selection is shown the input holds its label, so list everyone.
  const query = selected ? '' : normalize_query(text);
  const shown = useMemo(() => users.filter((u) => user_matches(u, query)), [users, query]);
  const menu_height = max_rows * ROW_HEIGHT;

  useLayoutEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const el = input_ref.current;
      if (!el) return;
      const w = el.getBoundingClientRect().width || width;
      set_pos(popover_position(el, { width: w, height: menu_height + 8, align: 'left' }));
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, width, menu_height]);

  useEffect(() => {
    if (!open) return undefined;
    const on_down = (e: MouseEvent) => {
      if (root_ref.current && !root_ref.current.contains(e.target as Node)) set_open(false);
    };
    document.addEventListener('mousedown', on_down);
    return () => document.removeEventListener('mousedown', on_down);
  }, [open]);

  useEffect(() => {
    if (active < 0) return;
    const el = list_ref.current?.children[active] as HTMLElement | undefined;
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [active]);

  const option_id = (i: number) => `${id}-option-${i}`;
  const list_id = `${id}-listbox`;

  const pick = (u: ComboUser) => {
    set_text(user_option_label(u));
    shown_ref.current = u.uuid;
    set_open(false);
    set_active(-1);
    on_change(u.uuid);
  };

  const on_type = (next: string) => {
    set_text(next);
    set_open(true);
    set_active(-1);
    if (value) {
      shown_ref.current = '';
      on_change('');
    }
  };

  const on_key = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) {
        set_open(true);
        return;
      }
      if (!shown.length) return;
      const step = e.key === 'ArrowDown' ? 1 : -1;
      set_active((a) => Math.max(0, Math.min(shown.length - 1, a < 0 && step < 0 ? 0 : a + step)));
    } else if (e.key === 'Enter') {
      if (open && active >= 0 && shown[active]) {
        e.preventDefault();
        pick(shown[active]);
      }
    } else if (e.key === 'Escape') {
      if (open) {
        e.preventDefault();
        set_open(false);
        set_active(-1);
        input_ref.current?.focus();
      }
    }
  };

  const expanded = open && !disabled;

  return (
    <div ref={root_ref} style={{ position: 'relative', display: 'inline-block', width }}>
      <input
        ref={input_ref}
        id={id}
        type="text"
        role="combobox"
        aria-label={label}
        aria-autocomplete="list"
        aria-expanded={expanded}
        aria-controls={list_id}
        aria-activedescendant={expanded && active >= 0 ? option_id(active) : undefined}
        autoComplete="off"
        placeholder={placeholder}
        value={text}
        disabled={disabled}
        onChange={(e) => on_type(e.target.value)}
        onFocus={() => set_open(true)}
        onClick={() => set_open(true)}
        onKeyDown={on_key}
        style={INPUT_STYLE}
      />
      {expanded ? (
        <div
          className="rounded-md border shadow-lg"
          style={{
            position: 'fixed',
            ...(pos?.bottom !== undefined ? { bottom: pos.bottom } : { top: pos?.top ?? 0 }),
            left: pos?.left ?? 0,
            width: input_ref.current?.getBoundingClientRect().width || width,
            visibility: pos ? 'visible' : 'hidden',
            zIndex: 1000,
            background: 'var(--surface-card)',
            borderColor: 'var(--border-default)'
          }}
        >
          <ul
            ref={list_ref}
            id={list_id}
            role="listbox"
            aria-label={label}
            style={{ maxHeight: menu_height, overflowY: 'auto', margin: 0, padding: 0 }}
          >
            {shown.map((u, i) => (
              // Keyboard selection is handled on the combobox input (aria-activedescendant).
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events
              <li
                key={u.uuid}
                id={option_id(i)}
                role="option"
                aria-selected={u.uuid === value}
                // Keep focus in the input so the click lands before blur logic runs.
                onMouseDown={(e) => e.preventDefault()}
                onMouseMove={() => active !== i && set_active(i)}
                onClick={() => pick(u)}
                className="truncate px-3 text-left text-sm"
                style={{
                  listStyle: 'none',
                  lineHeight: `${ROW_HEIGHT}px`,
                  cursor: 'pointer',
                  color: 'var(--text-primary)',
                  background: i === active ? 'var(--surface-sunken)' : 'transparent'
                }}
              >
                <Highlight text={user_option_label(u)} query={query} />
              </li>
            ))}
          </ul>
          {shown.length === 0 ? (
            <div className="px-3 py-2 text-sm" role="status" style={{ color: 'var(--text-muted)' }}>
              No matches
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default UserCombobox;
